import { inject } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import LZString from 'lz-string'
import Unauthorized from '@/components/Unauthorized.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import BaseModuleLayout from '@/layouts/BaseModuleLayout.vue'
import HomeSideMenu from '@/modules/home/components/HomeMenu.vue'
import InventorySideMenu from '@/modules/inventory/components/InventoryMenu.vue'
import OperationsSideMenu from '@/modules/operations/components/OperationsMenu.vue'
import SalesMarketingSideMenu from '@/modules/sales_marketing/components/SalesMarketingMenu.vue'
import AccountingSideMenu from '@/modules/accounting/components/AccountingMenu.vue'
import AdminSideMenu from '@/modules/admin/components/AdminMenu.vue'

const routes = [
  {
    path: '/login',
    component: AuthLayout,
    children: [{ path: '', name: 'login', component: () => import('@/modules/auth/views/Login.vue') }],
    meta: {public: true}
  },
  {
    path: '/signup',
    component: () => import('@/modules/auth/views/Signup.vue'),
    meta: {public: true}
  },
  {
    path: '/forgot-password',
    component: () => import('@/modules/auth/views/ForgotPassword.vue'),
    meta: {public: true}
  },
  {
    path: '/change-password',
    name: 'change-password',
    component: () => import('@/modules/auth/views/ChangePassword.vue'),
    meta: {public: true}
  },
  { path: '/unauthorized', component: Unauthorized },
  // catch-all for undefined routes (optional)
  { path: '/:pathMatch(.*)*', redirect: '/unauthorized' },
  {
    path: '/',
    component: DefaultLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '/home',
        name: 'home',
        component: BaseModuleLayout,
        redirect: '/home/release-notes',
        props: { sideMenuComponent: HomeSideMenu },
        children: [
          {
            path: 'release-notes',
            name: 'home-release-notes',
            component: () => import('@/modules/home/views/ReleaseNotes.vue')
          },
          {
            path: 'about',
            name: 'home-about',
            component: () => import('@/modules/home/views/About.vue')
          }
        ]
      },
      {
        path: '/operations',
        name: 'operations',
        component: BaseModuleLayout,
        redirect: '/operations/schedule',
        props: { sideMenuComponent: OperationsSideMenu },
        meta: { requiresAuth: true, permissions: ['operations.general.access']},
        children: [
          {
            path: 'schedule',
            name: 'operations-schedule',
            component: () => import('@/modules/operations/views/Schedule.vue')
          },
          {
            path: 'project-pipeline',
            name: 'operations-project-pipeline',
            component: () => import('@/modules/operations/views/ProjectPipeline.vue')
          },
          {
            path: 'driver-assignment',
            name: 'operations-driver-assignment',
            component: () => import('@/modules/operations/views/DriverAssignment.vue')
          },
          {
            path: 'permits',
            name: 'operations-permit-tracker',
            component: () => import('@/modules/operations/views/PermitTracker.vue')
          },
          {
            path: 'first-arrival-analysis',
            name: 'operations-first-arrival-analysis',
            component: () => import('@/modules/operations/views/FirstArrivalAnalysis.vue'),
            meta: { requiresAuth: true, permissions: ['operations.first_arrival.access']}
          },
          {
            path: 'company-costing',
            name: 'operations-company-costing',
            component: () => import('@/modules/operations/views/CompanyCosting.vue'),
            meta: { requiresAuth: true, permissions: ['operations.company_costing.access']}
          },
          {
            path: 'business-unit-costing',
            meta: { requiresAuth: true, permissions: ['operations.business_unit_costing.access']},
            children: [
              {
                path: '',
                name: 'operations-bu-costing',
                component: () => import('@/modules/operations/views/BusinessUnitCosting.vue'),
                props: true
              },
              {
                path: ':businessUnitId',
                name: 'operations-bu-costing-id',
                component: () => import('@/modules/operations/views/BusinessUnitCosting.vue'),
                props: (route) => ({
                  buId: route.params.businessUnitId,
                  startDate: route.query.start_date,
                  endDate: route.query.end_date
                })
              }
            ] 
          },
          {
            path: 'project-costing',
            meta: { requiresAuth: true, permissions: ['operations.project_costing.access']},
            children: [
              {
                path: '',
                name: 'operations-project-costing',
                component: () => import('@/modules/operations/views/ProjectCosting.vue'),
                props: true
              },
              {
                path: ':projectId',
                name: 'operations-project-costing-id',
                component: () => import('@/modules/operations/views/ProjectCosting.vue'),
                props: true
              }
            ] 
          },
          {
            path: 'job-costing',
            meta: { requiresAuth: true, permissions: ['operations.job_costing.access']},
            children: [
              {
                path: '',
                name: 'operations-job-costing',
                component: () => import('@/modules/operations/views/JobCosting.vue'),
                props: true
              },
              {
                path: ':jobId',
                name: 'operations-job-costing-id',
                component: () => import('@/modules/operations/views/JobCosting.vue'),
                props: true
              }
            ] 
          },
          {
            path: 'equipment-to-register',
            name: 'operations-equipment-to-register',
            component: () => import('@/modules/operations/views/EquipmentToRegister.vue'),
            meta: { requiresAuth: true, permissions: ['operations.warranty_registration.access']}
          },
          {
            path: 'pricebook-search',
            children: [
              {
                path: '',
                name: 'operations-pricebook-search',
                component: () => import('@/modules/operations/views/PricebookSearch.vue'),
                props: true
              },
              {
                path: ':itemId?',
                name: 'operations-pricebook-search-id',
                component: () => import('@/modules/operations/views/PricebookSearch.vue'),
                props: true
              }
            ] 
          }
        ]
      },
      {
        path: '/inventory',
        name: 'inventory',
        component: BaseModuleLayout,
        redirect: '/inventory/job-details',
        props: { sideMenuComponent: InventorySideMenu },
        meta: { requiresAuth: true, permissions: ['inventory.general.access']},
        children: [
          {
            path: 'job-details',
            children: [
              {
                path: '', // /inventory/job-details
                name: 'inventory-job-details',
                component: () => import('@/modules/inventory/views/JobDetails.vue'),
                props: true
              },
              {
                path: ':jobId', // /inventory/job-details/:jobId
                name: 'inventory-job-details-id',
                component: () => import('@/modules/inventory/views/JobDetails.vue'),
                props: true
              }
            ]
          },
          {
            path: 'scheduled-jobs',
            name: 'inventory-scheduled-jobs',
            component: () => import('@/modules/inventory/views/ScheduledJobs.vue'),
            meta: { requiresAuth: true, permissions: ['inventory.scheduled_jobs.access']},
          },
          {
            path: 'analysis',
            name: 'inventory-analysis',
            component: () => import('@/modules/inventory/views/InventoryAnalysis.vue'),
            meta: { requiresAuth: true, permissions: ['inventory.analysis.access']},
          },
          {
            path: 'create/:transactionType',
            name: 'inventory-create-transaction',
            component: () => import('@/modules/inventory/components/CreateTransaction.vue'),
            props: (route) => ({
              transactionType: route.params.transactionType,
              picklistItems: route.query.picklistItems ? JSON.parse(route.query.picklistItems) : [],
              jobNumber: route.query.jobNumber || '',
              jobBusinessUnit: route.query.jobBusinessUnit || ''
            })
          },
          {
            path: 'search-item',
            name: 'inventory-seach-item',
            component: () => import('@/modules/inventory/views/SearchItem.vue')
          },
          {
            path: 'count',
            children: [
              {
                path: '',
                name: 'inventory-count',
                component: () => import('@/modules/inventory/views/CreateCount.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.count.access']}
              },
              {
                path: 'create',
                name: 'inventory-count-create',
                component: () => import('@/modules/inventory/components/CreateTransaction.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.count.create']},
                props: (route) => ({
                  transactionType: 'count'
                })
              },
              {
                path: 'bulk-create',
                name: 'inventory-count-bulk-create',
                component: () => import('@/modules/inventory/views/CreateCountBulk.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.count.bulk_create']}
              },
              {
                path: 'edit/:id',
                name: 'inventory-edit-transaction',
                component: () => import('@/modules/inventory/components/CreateTransaction.vue'),
                props: (route) => ({
                  transactionType: 'edit-count',
                  transactionId: parseInt(route.params.id, 10), // pass the ID as a number
                })
              },
            ]
          },
          {
            path: 'order',
            children: [
              {
                path: '',
                name: 'inventory-order',
                component: () => import('@/modules/inventory/views/CreateOrder.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.order.access']}
              },
              {
                path: 'create',
                name: 'inventory-order-create',
                component: () => import('@/modules/inventory/components/CreateTransaction.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.order.create']},
                props: (route) => ({
                  transactionType: 'order'
                })
              },
            ]
          },
          {
            path: 'transfer',
            children: [
              {
                path: '',
                name: 'inventory-transfer',
                component: () => import('@/modules/inventory/views/CreateTransfer.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.transfer.access']}
              },
              {
                path: 'create',
                name: 'inventory-transfer-create',
                component: () => import('@/modules/inventory/components/CreateTransaction.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.transfer.create']},
                props: (route) => ({
                  transactionType: 'transfer'
                })
              },
              {
                path: 'create/picklist-transfer',
                name: 'inventory-transfer-create-picklist',
                component: () => import('@/modules/inventory/components/CreateTransaction.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.transfer.create'] },
                props: (route) => {
                  let raw = []
                  if (route.query.existingItems) {
                    try {
                      const decompressed = LZString.decompressFromEncodedURIComponent(route.query.existingItems)
                      raw = decompressed ? JSON.parse(decompressed) : []
                    } catch (err) {
                      console.error('Failed to parse existingItems', err)
                    }
                  }

                  const existingItems = raw.map(([skuId, due]) => ({ skuId, due }))

                  return {
                    transactionType: 'picklist-transfer',
                    existingItems,
                    referenceNumber: route.query.referenceNumber,
                    jobBusinessUnit: route.query.jobBusinessUnit || ''
                  }
                }
              },
              {
                path: 'create/replenishment-transfer',
                name: 'inventory-transfer-create-replenishment',
                component: () => import('@/modules/inventory/components/CreateTransaction.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.transfer.create'] },
                props: (route) => {
                  let raw = []
                  if (route.query.existingItems) {
                    try {
                      const decompressed = LZString.decompressFromEncodedURIComponent(route.query.existingItems)
                      raw = decompressed ? JSON.parse(decompressed) : []
                    } catch (err) {
                      console.error('Failed to parse existingItems', err)
                    }
                  }

                  const existingItems = raw.map(([skuId, due]) => ({ skuId, due }))

                  return {
                    transactionType: 'replenishment-transfer',
                    existingItems,
                    referenceNumber: 'Replenishment',
                    replenishTo: Number(route.query.replenishTo)
                  }
                }
              },
              {
                path: 'edit/:id',
                name: 'inventory-transfer-edit',
                component: () => import('@/modules/inventory/components/CreateTransaction.vue'),
                props: (route) => ({
                  transactionType: 'edit-transfer',
                  transactionId: parseInt(route.params.id, 10)
                })
              }
            ]
          },
          {
            path: 'label',
            children: [
              {
                path: '',
                name: 'inventory-label',
                component: () => import('@/modules/inventory/views/CreateLabel.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.label.access']}
              },
              {
                path: 'create',
                name: 'inventory-label-create',
                component: () => import('@/modules/inventory/components/CreateTransaction.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.label.create']},
                props: (route) => ({
                  transactionType: 'label'
                })
              },
            ]
          },
          {
            path: 'replenishment',
            children: [
              {
                path: '',
                name: 'inventory-replenishment',
                component: () => import('@/modules/inventory/views/ReplenishmentDashboard.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.replenishment.access']}
              },
              {
                path: 'create',
                name: 'inventory-replenishment-create',
                component: () => import('@/modules/inventory/views/CreateReplenishment.vue'),
                meta: { requiresAuth: true, permissions: ['inventory.label.create']},
                props: (route) => ({
                  transactionType: 'label'
                })
              },
            ]
          },
          {
            path: 'invoice-closeout',
            name: 'inventory-invoice-closeout',
            component: () => import('@/modules/inventory/views/InvoiceCloseout.vue'),
            meta: { requiresAuth: true, permissions: ['inventory.invoice_closeout.access']}
          },
          {
            path: 'cost-audit',
            name: 'inventory-cost-audit',
            component: () => import('@/modules/inventory/views/CostAudit.vue'),
            meta: { requiresAuth: true, permissions: ['inventory.cost_audit.access']}
          }
        ]
      },
      {
        path: '/admin',
        name: 'admin',
        component: BaseModuleLayout,
        redirect: '/admin/manage-users',
        props: { sideMenuComponent: AdminSideMenu },
        meta: { requiresAuth: true, permissions: ['admin.general.access']},
        children: [
          {
            path: 'manage-users',
            name: 'admin-manage-users',
            component: () => import('@/modules/admin/views/ManageUsers.vue')
          },
          {
            path: 'manage-roles',
            children: [
              {
                path: '',
                name: 'admin-manage-roles',
                component: () => import('@/modules/admin/views/ManageRoles.vue')
              },
              {
                path: 'edit/:id',
                name: 'admin-manage-roles-edit',
                component: () => import('@/modules/admin/views/RoleEditor.vue'),
              },
              {
                path: 'new',
                name: 'admin-create-role',
                component: () => import('@/modules/admin/views/RoleEditor.vue'),
              }
            ]
          },
          {
            path: 'manage-permissions',
            name: 'admin-manage-permissions',
            component: () => import('@/modules/admin/views/ManagePermissions.vue')
          },
          {
            path: 'manage-password-resets',
            name: 'admin-manage-password-resets',
            component: () => import('@/modules/admin/views/ManagePasswordResets.vue')
          },
          {
            path: 'config-operations',
            name: 'admin-config-operations',
            component: () => import('@/modules/admin/views/ConfigOperations.vue')
          },
          {
            path: 'config-inventory',
            name: 'admin-config-inventory',
            component: () => import('@/modules/admin/views/ConfigInventory.vue')
          }
        ]
      },
      {
        path: '/sales-marketing',
        name: 'sales_marketing',
        component: BaseModuleLayout,
        redirect: '/sales-marketing/analytics/dashboard',
        props: { sideMenuComponent: SalesMarketingSideMenu },
        meta: { requiresAuth: true, permissions: ['sales_marketing.general.access']},
        children: [
          {
            path: 'analytics',
            children: [
              {
                path: 'dashboard', // /inventory/job-details
                name: 'sales_marketing-dashboard',
                component: () => import('@/modules/sales_marketing/views/SalesMarketingDashboard.vue'),
                props: true
              }
            ]
          },
        ]
      },
      {
        path: '/accounting',
        name: 'accounting',
        component: BaseModuleLayout,
        redirect: '/accounting/rebates',
        props: { sideMenuComponent: AccountingSideMenu },
        meta: { requiresAuth: true, permissions: ['accounting.general.access']},
        children: [
          {
            path: 'payroll',
            name: 'accounting-payroll',
            component: () => import('@/modules/accounting/views/Payroll.vue'),
            meta: { requiresAuth: true, permissions: ['accounting.payroll.access']}
          },
          {
            path: 'over-under-billing',
            name: 'accounting-over-under-billing',
            component: () => import('@/modules/accounting/views/OverUnderBilling.vue')
          },
          {
            path: 'project-commission-analysis',
            name: 'accounting-project-commission-analysis',
            component: () => import('@/modules/accounting/views/ProjectCommissionAnalysis.vue')
          },
          {
            path: 'rebates',
            name: 'accounting-rebates',
            component: () => import('@/modules/accounting/views/Rebates.vue')
          }
        ]
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to, from, next) => {
  const auth = inject('auth')

  // skip public endpoints
  if (to.meta.public) return next()

  try {
    // 🔹 1. Check session validity
    const response = await fetch(`${process.env.VUE_APP_API_BASE}/check-session`)

    if (!response.ok) {
      return next('/login')
    }

    // 🔹 2. Get user permissions (from backend)
    const data = await response.json()
    auth.setPermissions(data.permissions || [])

    // 🔹 3. Check route meta requirements
    if (to.meta && to.meta.permissions) {
      const hasAll = to.meta.permissions.every(p => auth.hasPermission(p))
      if (!hasAll) {
        return next('/unauthorized') // or redirect somewhere safe
      }
    }

    // 🔹 4. Default redirect
    if (to.path === '/') {
      return next('/home')
    }

    return next()
  } catch (err) {
    console.error('Session check failed', err)
    return next('/login')
  }
})

export default router